import { useEffect, useRef, useState } from 'react';

import { useAuthStore } from '../stores';
import { fetchYourTourApi } from '../utils/ApiHelpers';

const SS_VERDICT_KEY = 'ytFathomExcludeVerdict';
const SS_FORCE_KEY = 'ytFathomExclude';
const ENDPOINT = '/your_tour_api/fathom-exclude-check?_format=json';
const TIMEOUT_MS = 1000;

const useFathomExclusion = ({ enabled, platform }) => {
  const [state, setState] = useState({ excluded: false, ready: false });
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (checkedRef.current) return undefined;
    checkedRef.current = true;

    // Sticky exclude flag: ?ref=admin link from end-personalization screen,
    // or a prior decision in this tab.
    const params = new URLSearchParams(window.location.search);
    if (params.get('ref') === 'admin') {
      sessionStorage.setItem(SS_FORCE_KEY, '1');
    }
    if (sessionStorage.getItem(SS_FORCE_KEY) === '1') {
      setState({ excluded: true, ready: true });
      return undefined;
    }

    if (isLoggedIn) {
      setState({ excluded: true, ready: true });
      return undefined;
    }

    const cached = sessionStorage.getItem(SS_VERDICT_KEY);
    if (cached === 'exclude' || cached === 'include') {
      setState({ excluded: cached === 'exclude', ready: true });
      return undefined;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // Fail-open: if Drupal is slow, prefer tracking over silently dropping.
      setState({ excluded: false, ready: true });
    }, TIMEOUT_MS);

    fetchYourTourApi(ENDPOINT, platform)
      .then((data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        const excluded = !!data?.exclude;
        sessionStorage.setItem(SS_VERDICT_KEY, excluded ? 'exclude' : 'include');
        setState({ excluded, ready: true });
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setState({ excluded: false, ready: true });
      });

    return () => {
      clearTimeout(timer);
    };
  }, [enabled, platform, isLoggedIn]);

  return state;
};

export default useFathomExclusion;
